import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRightIcon, XIcon } from "../../utils/icons";
import { useState } from "react";

// Hair Care Tips data
const hairCareTips = [
  {
    id: 1,
    title: "Deep Conditioning Magic",
    shortDescription:
      "Transform your hair's health with weekly deep conditioning",
    description:
      "Deep conditioning is crucial for maintaining healthy natural hair. Apply your deep conditioner to clean, damp hair and use a heating cap for better penetration. Leave it on for 15-30 minutes for best results.",
    image:
      "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1741946380/IMG_9547_sdmx5k.jpg",
    category: "Treatment",
    readTime: "3 min read",
  },
  {
    id: 2,
    title: "Protective Styling Guide",
    shortDescription:
      "Master the art of protective styling for length retention",
    description:
      "Protective styles help retain length by keeping your ends tucked away. Ensure your hair is well-moisturized before styling and don't install styles too tight.",
    image:
      "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1741946456/IMG_9533_vmkaby.jpg",
    category: "Styling",
    readTime: "4 min read",
  },
  {
    id: 3,
    title: "Nighttime Hair Care Routine",
    shortDescription: "Protect your hair while you sleep for better hair days",
    description:
      "Use a silk or satin pillowcase and bonnet to prevent friction and moisture loss while you sleep. This simple step can significantly reduce breakage.",
    image:
      "https://res.cloudinary.com/dmxzxo1fk/image/upload/v1741946266/IMG_9572_yg7syq.jpg",
    category: "Maintenance",
    readTime: "2 min read",
  },
];

export default function HairCareTips() {
  const [selectedTip, setSelectedTip] = useState(null);

  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#DB5461]/10 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 mb-4 text-sm font-medium tracking-wider text-gold-500 bg-gold-50 rounded-full">
            HAIR CARE TIPS
          </span>
          <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
            Expert Hair Care Tips
          </h2>
          <div className="w-24 h-1 bg-[#DB5461] mx-auto rounded-full" />
        </motion.div>

        {/* Tips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {hairCareTips.map((tip, index) => (
            <motion.div
              key={tip.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ y: -8 }}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500"
            >
              {/* Image Container */}
              <div className="relative h-48 overflow-hidden">
                <motion.img
                  src={tip.image}
                  alt={tip.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 text-xs font-medium text-white bg-[#DB5461]/90 rounded-full">
                    {tip.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2 group-hover:text-[#DB5461] transition-colors duration-300">
                    {tip.title}
                  </h3>
                  <p className="text-gray-600 line-clamp-2">
                    {tip.shortDescription}
                  </p>
                </div>

                {/* <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{tip.readTime}</span>
                  <button
                    onClick={() => setSelectedTip(tip)}
                    className="text-[#DB5461] flex items-center gap-1 text-sm font-medium group"
                  >
                    <motion.div
                      whileHover={{ x: 4 }}
                      className="flex items-center gap-1"
                    >
                      Read More
                      <ArrowRightIcon className="w-4 h-4" />
                    </motion.div>
                  </button>
                </div> */}
              </div>

              {/* Decorative Elements */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#DB5461]/20 rounded-2xl transition-all duration-300" />
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/tips"
            className="inline-flex items-center gap-2 px-6 py-3 text-white bg-[#DB5461] rounded-full hover:bg-[#DB5461]/90 transition-colors duration-300"
          >
            <span>View All Hair Care Tips</span>
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </motion.div>

        {/* Modal */}
        <AnimatePresence>
          {selectedTip && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
              onClick={() => setSelectedTip(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl overflow-hidden max-w-2xl w-full shadow-2xl relative"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedTip(null)}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
                >
                  <XIcon className="w-6 h-6" />
                </button>

                {/* Modal Content */}
                <div className="relative">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={selectedTip.image}
                      alt={selectedTip.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  </div>

                  <div className="p-8">
                    <span className="inline-block px-3 py-1 text-xs font-medium text-white bg-[#DB5461] rounded-full mb-4">
                      {selectedTip.category}
                    </span>
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                      {selectedTip.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {selectedTip.description}
                    </p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <span>{selectedTip.readTime}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
